import { useEffect, useRef, useState } from 'react'
import { PriceFormatter, UserIcon } from '../../'
import { trackWithExtra } from '../../../../lib/tracking'
import { colors } from '../../../colors'
import { FREE_THEME_ID } from '../../../constants'
import rightArrowIcon from '../../../images/right-arrow.png'
import GuidelinesLink from '../../ui/GuidelinesLink'
import GuidelinesModal from '../../ui/GuidelinesModal'
import type { FormData } from './utils/formValidation'
import type { BargainUserInfo, Theme } from '../../../types'

interface PostPreviewProps {
  formData: FormData
  shopName: string
  chainName: string
  chainLogoUrl: string | null
  bargain_user_info: BargainUserInfo
  imagePreviews: string[]
  loading: boolean
  onBackToEdit: () => void
  onSubmit: () => void
  currentTheme?: Theme
  currentThemeId?: number
}

// PostCard.tsxと同じスタイルを適用
const styles: Record<string, React.CSSProperties> = {
  postCard: {
    border: `1px solid ${colors['border-light']}`,
    borderRadius: '20px',
    backgroundColor: colors.white,
    padding: '12px',
    marginBottom: '8px',
  },
  postHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  userName: {
    fontSize: '14px',
    color: colors['text-secondary'],
  },
  shopInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px',
  },
  logoImage: {
    padding: '4px',
    width: '46px',
    height: '46px',
    objectFit: 'contain',
    marginRight: '8px',
  },
  shopName: {
    fontSize: '14px',
    color: colors['text-secondary'],
    marginRight: '4px',
  },
  productName: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '4px',
    color: colors['text-default'],
  },
  postText: {
    fontSize: '16px',
    color: colors['text-default'],
    marginBottom: '4px',
    whiteSpace: 'pre-wrap',
  },
  postImages: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '8px',
    marginBottom: '8px',
  },
  postImagesOne: {
    width: '100%',
  },
  postImagesTwo: {
    width: '100%',
  },
  postImagesThree: {
    width: '100%',
    height: '280px',
    display: 'grid',
    gridTemplateColumns: '60% 40%',
    gridTemplateRows: '1fr 1fr',
    gap: '4px',
  },
  postImage: {
    overflow: 'hidden',
  },
  postImageOne: {
    width: '100%',
    maxHeight: '320px',
    height: 'auto',
  },
  postImageTwo: {
    width: 'calc(50% - 2px)',
    aspectRatio: '1',
  },
  postImageThreeFirst: {
    gridColumn: '1',
    gridRow: '1 / 3',
    height: '100%',
  },
  postImageThreeSecond: {
    gridColumn: '2',
    gridRow: '1',
    height: '100%',
  },
  postImageThreeThird: {
    gridColumn: '2',
    gridRow: '2',
    height: '100%',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    cursor: 'pointer',
  },
  imageOne: {
    width: '100%',
    height: 'auto',
    maxHeight: '400px',
    objectFit: 'contain',
    objectPosition: 'left',
    display: 'block',
    cursor: 'pointer',
  },
  postFooter: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px',
    fontSize: '12px',
    color: colors['text-secondary'],
    gap: '4px',
  },
  previewActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '16px',
  },
  submitButton: {
    padding: '16px 24px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    width: '100%',
  },
  backButton: {
    padding: '16px 24px',
    backgroundColor: 'transparent',
    color: colors['text-secondary'],
    border: `1px solid ${colors['border-light']}`,
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
  },
  rightArrowIcon: {
    width: '16px',
    marginBottom: '3px',
    marginRight: '2px',
  },
  disabledButton: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  // モーダル用スタイル
  fullscreenOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    cursor: 'pointer',
  },
  modalContainer: {
    position: 'relative',
    width: '100vw',
    height: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageSlider: {
    display: 'flex',
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    width: '100%',
    height: '100%',
    gap: '20px',
    padding: '0',
  },
  slideImage: {
    width: '100vw',
    maxHeight: '100%',
    objectFit: 'contain',
    scrollSnapAlign: 'center',
    flexShrink: 0,
  },
  closeButton: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    fontSize: '40px',
    color: 'white',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}

const PostPreview: React.FC<PostPreviewProps> = ({
  formData,
  shopName,
  chainName,
  chainLogoUrl,
  bargain_user_info,
  imagePreviews,
  loading,
  onBackToEdit,
  onSubmit,
  currentTheme,
  currentThemeId,
}) => {
  // ガイドラインモーダルの状態管理
  const [isGuidelinesModalOpen, setIsGuidelinesModalOpen] = useState(false)

  // 画像モーダルの状態管理
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const imageSliderRef = useRef<HTMLDivElement>(null)

  // ガイドラインモーダルのハンドラー
  const handleOpenGuidelinesModal = () => {
    void trackWithExtra('bargain.message_board.guidelines_link.click', {
      path: location.pathname,
    })
    setIsGuidelinesModalOpen(true)
  }

  const handleCloseGuidelinesModal = () => {
    setIsGuidelinesModalOpen(false)
  }

  // 画像モーダルのハンドラー
  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
  }

  const handleCloseImageModal = () => {
    setSelectedImageIndex(null)
  }

  // モーダルが開いたときに選択された画像にスクロール
  useEffect(() => {
    if (selectedImageIndex !== null && imageSliderRef.current) {
      const slider = imageSliderRef.current
      const imageWidth = slider.scrollWidth / imagePreviews.length
      const scrollPosition = selectedImageIndex * imageWidth

      slider.scrollLeft = scrollPosition
    }
  }, [selectedImageIndex, imagePreviews.length])

  return (
    <div>
      <div style={styles.postCard}>
        {/* ユーザー情報ヘッダー */}
        <div style={styles.postHeader}>
          <div style={styles.userInfo}>
            <UserIcon />
            <div style={styles.userName}>
              {bargain_user_info.name || formData.posterName || 'ユーザー名未設定'}
            </div>
          </div>
        </div>

        {/* 投稿画像リスト（PostCard.tsxと同じ構造・スタイル） */}
        {imagePreviews.length > 0 && (
          <div
            style={{
              ...styles.postImages,
              ...(imagePreviews.length === 1 ? styles.postImagesOne : {}),
              ...(imagePreviews.length === 2 ? styles.postImagesTwo : {}),
              ...(imagePreviews.length >= 3 ? styles.postImagesThree : {}),
            }}
          >
            {imagePreviews.map((preview, idx) => (
              <div
                key={idx}
                style={{
                  ...styles.postImage,
                  ...(imagePreviews.length === 1 ? styles.postImageOne : {}),
                  ...(imagePreviews.length === 2 ? styles.postImageTwo : {}),
                  ...(imagePreviews.length >= 3 ? styles.postImageThree : {}),
                  ...(imagePreviews.length === 3 && idx === 0 ? styles.postImageThreeFirst : {}),
                  ...(imagePreviews.length === 3 && idx === 1 ? styles.postImageThreeSecond : {}),
                  ...(imagePreviews.length === 3 && idx === 2 ? styles.postImageThreeThird : {}),
                }}
              >
                <img
                  src={preview}
                  alt={formData.productName}
                  style={imagePreviews.length === 1 ? styles.imageOne : styles.image}
                  onClick={() => handleImageClick(idx)}
                />
              </div>
            ))}
          </div>
        )}

        <div>
          {/* 店舗情報 */}
          {(chainName || shopName) && (
            <div style={styles.shopInfo}>
              {chainLogoUrl && <img src={chainLogoUrl} style={styles.logoImage} alt={chainName} />}
              <div style={styles.shopName}>
                {chainName} {shopName}
              </div>
            </div>
          )}

          {/* 商品名 */}
          <h3 style={styles.productName}>{formData.productName}</h3>

          {/* コメント */}
          <p style={styles.postText}>{formData.comment}</p>

          {/* テーマハッシュタグ */}
          {currentTheme && currentThemeId !== FREE_THEME_ID && (
            <div
              style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: '600',
                lineHeight: '1.0',
                color: colors['keijiban-coral'],
                textDecoration: 'none',
              }}
            >
              #{currentTheme.title}
            </div>
          )}

          {/* 価格 */}
          {formData.price && (
            <div style={styles.postFooter}>
              <PriceFormatter price={Number.parseFloat(formData.price)} />
            </div>
          )}
        </div>
      </div>

      {/* ガイドラインリンク */}
      <GuidelinesLink onClick={handleOpenGuidelinesModal} />

      {/* アクションボタン */}
      <div style={styles.previewActions}>
        <button type="button" onClick={onSubmit} style={styles.submitButton} disabled={loading}>
          {loading ? (
            '送信中...'
          ) : (
            <>
              <img src={rightArrowIcon} style={styles.rightArrowIcon} alt="right-arrow" />
              投稿する
            </>
          )}
        </button>
        <button type="button" onClick={onBackToEdit} style={styles.backButton} disabled={loading}>
          編集に戻る
        </button>
      </div>

      {/* ガイドラインモーダル */}
      <GuidelinesModal isOpen={isGuidelinesModalOpen} onClose={handleCloseGuidelinesModal} />

      {/* 画像モーダル */}
      {selectedImageIndex !== null && imagePreviews.length > 0 && (
        <div style={styles.fullscreenOverlay} onClick={handleCloseImageModal}>
          <div style={styles.modalContainer}>
            <div ref={imageSliderRef} style={styles.imageSlider}>
              {imagePreviews.map((preview) => (
                <img
                  key={preview}
                  src={preview}
                  alt={formData.productName}
                  style={styles.slideImage}
                  onClick={(e) => e.stopPropagation()}
                />
              ))}
            </div>
            <button
              type="button"
              style={styles.closeButton}
              onClick={(e) => {
                e.stopPropagation()
                handleCloseImageModal()
              }}
              aria-label="モーダルを閉じる"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PostPreview
